import React from 'react';
import {
  Button,
  IconButton,
  Link,
  NumberInput,
  ScreenContainer,
  SimpleStyleKeyboardAwareScrollView,
  SimpleStyleScrollView,
  TextInput,
  withTheme,
} from '@draftbit/ui';
import { useIsFocused } from '@react-navigation/native';
import { Image, Text, View } from 'react-native';
import * as GlobalStyles from '../GlobalStyles.js';
import * as AuthenticationApi from '../apis/AuthenticationApi.js';
import * as GlobalVariables from '../config/GlobalVariableContext';
import Images from '../config/Images';
import * as Confetti from '../custom-files/Confetti';
import checkExpires from '../global-functions/checkExpires';
import checkScreenMode from '../global-functions/checkScreenMode';
import formatDate from '../global-functions/formatDate';
import newFormatDate from '../global-functions/newFormatDate';
import resetGlobals from '../global-functions/resetGlobals';
import palettes from '../themes/palettes';
import * as Utils from '../utils';
import Breakpoints from '../utils/Breakpoints';
import * as StyleSheet from '../utils/StyleSheet';
import imageSource from '../utils/imageSource';
import useWindowDimensions from '../utils/useWindowDimensions';
import waitUtil from '../utils/wait';

const LoginScreen = props => {
  const { theme, navigation } = props;
  const dimensions = useWindowDimensions();
  const Constants = GlobalVariables.useValues();
  const Variables = Constants;
  const setGlobalVariableValue = GlobalVariables.useSetValue();
  const [email, setEmail] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [lastName, setLastName] = React.useState('');
  const [numberInputValue, setNumberInputValue] = React.useState('');
  const [otp, setOtp] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [sendNew, setSendNew] = React.useState(false);
  const [textInputValue, setTextInputValue] = React.useState('');
  const [verifyFlow, setVerifyFlow] = React.useState(false);
  const [visiblePassword, setVisiblePassword] = React.useState(false);
  const inputValid = () => {
    let foundError = false;

    if (email.length < 1) {
      setErrorMessage('Please enter your email');
      foundError = true;
      return foundError;
    } else {
      setErrorMessage('');
    }

    if (password.length < 1) {
      setErrorMessage('Please enter a password');
      foundError = true;
      return foundError;
    } else {
      setErrorMessage('');
    }

    return foundError;
  };
  const authenticationInitiateLoginVerificationPOST =
    AuthenticationApi.useInitiateLoginVerificationPOST();
  const authenticationLoginPOST = AuthenticationApi.useLoginPOST();
  const authenticationVerifyOTPPOST = AuthenticationApi.useVerifyOTPPOST();
  const isFocused = useIsFocused();
  React.useEffect(() => {
    const handler = async () => {
      try {
        if (!isFocused) {
          return;
        }
        setVisiblePassword(false);
        setEmail(Constants['USER_EMAIL']);
        if (!(dimensions.width >= Breakpoints.Tablet)) {
          return;
        }
        if (Constants['AUTH_TOKEN']) {
          const response = (await AuthenticationApi.authMeGET(Constants))?.json;
          if (response?.id) {
            if (response?.verified) {
              if (response?.subscription_plans_id) {
                if (checkExpires(response?.plan_expires_at)) {
                  navigation.navigate('PaymentPlanSelectionScreen');
                } else {
                  navigation.navigate('DashboardScreen');
                }
              } else {
                navigation.navigate('PaymentPlanSelectionScreen');
              }
            } else {
              navigation.navigate('AccountVerificationScreen', {
                screen_from: 'login',
                subscription_plans_id: response?.subscription_plans_id,
                expires_at: response?.plan_expires_at,
              });
            }
          } else {
          }
        } else {
        }
      } catch (err) {
        console.error(err);
      }
    };
    handler();
  }, [isFocused]);

  return (
    <ScreenContainer
      hasSafeArea={false}
      scrollable={false}
      style={StyleSheet.applyWidth(
        { backgroundColor: theme.colors.background.brand },
        dimensions.width
      )}
    >
      {/* Container */}
      <>
        {!(dimensions.width >= Breakpoints.Laptop) ? null : (
          <SimpleStyleScrollView
            bounces={true}
            horizontal={false}
            keyboardShouldPersistTaps={'never'}
            nestedScrollEnabled={false}
            showsHorizontalScrollIndicator={true}
            showsVerticalScrollIndicator={true}
            style={StyleSheet.applyWidth(
              {
                alignItems: [
                  { minWidth: Breakpoints.Laptop, value: 'center' },
                  { minWidth: Breakpoints.Mobile, value: 'center' },
                ],
                flex: [
                  { minWidth: Breakpoints.Laptop, value: 1 },
                  { minWidth: Breakpoints.Mobile, value: 1 },
                ],
                gap: [
                  { minWidth: Breakpoints.Laptop, value: 20 },
                  { minWidth: Breakpoints.Mobile, value: 20 },
                ],
              },
              dimensions.width
            )}
          >
            {/* Logo */}
            <View
              style={StyleSheet.applyWidth(
                {
                  alignItems: { minWidth: Breakpoints.Laptop, value: 'center' },
                  height: { minWidth: Breakpoints.Laptop, value: '33%' },
                  justifyContent: {
                    minWidth: Breakpoints.Laptop,
                    value: 'center',
                  },
                },
                dimensions.width
              )}
            >
              {/* Image 2 */}
              <Image
                {...GlobalStyles.ImageStyles(theme)['Image'].props}
                resizeMode={'contain'}
                source={imageSource(Images['logocropped'])}
                style={StyleSheet.applyWidth(
                  StyleSheet.compose(
                    GlobalStyles.ImageStyles(theme)['Image'].style,
                    {
                      height: [
                        { minWidth: Breakpoints.Laptop, value: 200 },
                        { minWidth: Breakpoints.Desktop, value: 200 },
                        { minWidth: Breakpoints.BigScreen, value: 250 },
                        { minWidth: Breakpoints.Mobile, value: 200 },
                      ],
                      width: [
                        { minWidth: Breakpoints.Laptop, value: 200 },
                        { minWidth: Breakpoints.Desktop, value: 200 },
                        { minWidth: Breakpoints.BigScreen, value: 250 },
                        { minWidth: Breakpoints.Mobile, value: 200 },
                      ],
                    }
                  ),
                  dimensions.width
                )}
              />
            </View>
            {/* Inputs */}
            <>
              {verifyFlow ? null : (
                <View
                  style={StyleSheet.applyWidth(
                    {
                      gap: { minWidth: Breakpoints.Laptop, value: 50 },
                      width: [
                        { minWidth: Breakpoints.Laptop, value: '25%' },
                        { minWidth: Breakpoints.BigScreen, value: '15%' },
                        { minWidth: Breakpoints.Desktop, value: '17%' },
                      ],
                    },
                    dimensions.width
                  )}
                >
                  {/* Email Input */}
                  <TextInput
                    autoCapitalize={'none'}
                    autoCorrect={true}
                    changeTextDelay={500}
                    onChangeText={newEmailInputValue => {
                      const textInputValue = newEmailInputValue;
                      try {
                        setEmail(newEmailInputValue);
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                    webShowOutline={true}
                    {...GlobalStyles.TextInputStyles(theme)['Text Input'].props}
                    autoComplete={'email'}
                    placeholder={'Enter email'}
                    style={StyleSheet.applyWidth(
                      StyleSheet.compose(
                        GlobalStyles.TextInputStyles(theme)['Text Input'].style,
                        {
                          paddingBottom: {
                            minWidth: Breakpoints.Desktop,
                            value: null,
                          },
                          paddingTop: {
                            minWidth: Breakpoints.Desktop,
                            value: null,
                          },
                        }
                      ),
                      dimensions.width
                    )}
                    value={email}
                  />
                  <View>
                    {/* Password Input */}
                    <TextInput
                      autoCapitalize={'none'}
                      autoCorrect={true}
                      changeTextDelay={500}
                      onChangeText={newPasswordInputValue => {
                        try {
                          setPassword(newPasswordInputValue);
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                      webShowOutline={true}
                      {...GlobalStyles.TextInputStyles(theme)['Text Input']
                        .props}
                      autoComplete={'current-password'}
                      keyboardType={'visible-password'}
                      placeholder={'Enter password'}
                      secureTextEntry={!visiblePassword}
                      style={StyleSheet.applyWidth(
                        StyleSheet.compose(
                          GlobalStyles.TextInputStyles(theme)['Text Input']
                            .style,
                          {
                            paddingRight: {
                              minWidth: Breakpoints.Laptop,
                              value: 32,
                            },
                          }
                        ),
                        dimensions.width
                      )}
                      value={password}
                    />
                    <IconButton
                      onPress={() => {
                        try {
                          setVisiblePassword(!visiblePassword);
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                      size={32}
                      color={StyleSheet.getWidthValue(
                        [
                          {
                            minWidth: Breakpoints.Laptop,
                            value: palettes.Brand.Black,
                          },
                        ],
                        dimensions.width
                      )}
                      icon={
                        visiblePassword
                          ? 'FontAwesome/eye'
                          : 'FontAwesome/eye-slash'
                      }
                      size={StyleSheet.getWidthValue(
                        [{ minWidth: Breakpoints.Laptop, value: 24 }],
                        dimensions.width
                      )}
                      style={StyleSheet.applyWidth(
                        {
                          position: {
                            minWidth: Breakpoints.Laptop,
                            value: 'absolute',
                          },
                          right: { minWidth: Breakpoints.Laptop, value: 8 },
                          top: { minWidth: Breakpoints.Laptop, value: 6 },
                        },
                        dimensions.width
                      )}
                    />
                  </View>
                  {/* Forget Password Link */}
                  <Link
                    accessible={true}
                    onPress={() => {
                      try {
                        navigation.navigate('ForgotPasswordScreen');
                        setEmail('');
                        setPassword('');
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                    selectable={false}
                    {...GlobalStyles.LinkStyles(theme)['Link'].props}
                    style={StyleSheet.applyWidth(
                      StyleSheet.compose(
                        GlobalStyles.LinkStyles(theme)['Link'].style,
                        {
                          color: {
                            minWidth: Breakpoints.Laptop,
                            value: palettes.Brand['App Green'],
                          },
                          fontFamily: {
                            minWidth: Breakpoints.Laptop,
                            value: 'OpenSans_500Medium',
                          },
                          fontSize: [
                            { minWidth: Breakpoints.Desktop, value: 16 },
                            { minWidth: Breakpoints.Laptop, value: 16 },
                          ],
                          textAlign: {
                            minWidth: Breakpoints.Laptop,
                            value: 'center',
                          },
                          textTransform: {
                            minWidth: Breakpoints.Laptop,
                            value: 'lowercase',
                          },
                        }
                      ),
                      dimensions.width
                    )}
                    title={'forgot password'}
                  />
                </View>
              )}
            </>
            {/* Verify View */}
            <>
              {!verifyFlow ? null : (
                <View
                  style={StyleSheet.applyWidth(
                    {
                      alignItems: 'center',
                      gap: 50,
                      width: [
                        { minWidth: Breakpoints.Laptop, value: '25%' },
                        { minWidth: Breakpoints.BigScreen, value: '15%' },
                      ],
                    },
                    dimensions.width
                  )}
                >
                  <Text
                    accessible={true}
                    selectable={false}
                    {...GlobalStyles.TextStyles(theme)['Text'].props}
                    style={StyleSheet.applyWidth(
                      StyleSheet.compose(
                        GlobalStyles.TextStyles(theme)['Text'].style,
                        {
                          color: palettes.Brand.White,
                          fontFamily: 'OpenSans_500Medium',
                          textAlign: 'center',
                        }
                      ),
                      dimensions.width
                    )}
                  >
                    {
                      'For your security, please enter the code sent to your email. It is valid for 10 minutes.'
                    }
                  </Text>
                  <NumberInput
                    changeTextDelay={500}
                    onChangeText={newNumberInputValue => {
                      try {
                        setOtp(newNumberInputValue);
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                    webShowOutline={true}
                    {...GlobalStyles.NumberInputStyles(theme)['Number Input']
                      .props}
                    placeholder={'Enter OTP '}
                    style={StyleSheet.applyWidth(
                      StyleSheet.compose(
                        GlobalStyles.NumberInputStyles(theme)['Number Input']
                          .style,
                        {
                          backgroundColor: palettes.Brand.White,
                          color: palettes.Brand.Black,
                          fontFamily: 'OpenSans_500Medium',
                          fontSize: 16,
                        }
                      ),
                      dimensions.width
                    )}
                    value={otp}
                  />
                </View>
              )}
            </>
            {/* Buttons */}
            <>
              {verifyFlow ? null : (
                <View
                  style={StyleSheet.applyWidth(
                    {
                      alignItems: {
                        minWidth: Breakpoints.Laptop,
                        value: 'center',
                      },
                      marginTop: { minWidth: Breakpoints.Laptop, value: 20 },
                      width: { minWidth: Breakpoints.Laptop, value: '100%' },
                    },
                    dimensions.width
                  )}
                >
                  {/* Error Message */}
                  <Text
                    accessible={true}
                    selectable={false}
                    {...GlobalStyles.TextStyles(theme)['Text'].props}
                    style={StyleSheet.applyWidth(
                      StyleSheet.compose(
                        GlobalStyles.TextStyles(theme)['Text'].style,
                        {
                          color: {
                            minWidth: Breakpoints.Laptop,
                            value: palettes.Brand['App Red'],
                          },
                          fontFamily: {
                            minWidth: Breakpoints.Laptop,
                            value: 'OpenSans_500Medium',
                          },
                          fontSize: [
                            { minWidth: Breakpoints.Desktop, value: 18 },
                            { minWidth: Breakpoints.Laptop, value: 14 },
                          ],
                          textAlign: {
                            minWidth: Breakpoints.Laptop,
                            value: 'center',
                          },
                        }
                      ),
                      dimensions.width
                    )}
                  >
                    {errorMessage}
                  </Text>
                  {/* Login */}
                  <Button
                    accessible={true}
                    iconPosition={'left'}
                    onPress={() => {
                      const handler = async () => {
                        try {
                          if (inputValid()) {
                            return;
                          }
                          setErrorMessage('');
                          setIsLoading(true);
                          const loginResponse = (
                            await authenticationLoginPOST.mutateAsync({
                              email: email,
                              password: password,
                            })
                          )?.json;
                          if (loginResponse?.authToken) {
                            setGlobalVariableValue({
                              key: 'USER_EMAIL',
                              value: email,
                            });
                            setPassword('');
                            setGlobalVariableValue({
                              key: 'AUTH_TOKEN',
                              value: 'Bearer ' + loginResponse?.authToken,
                            });
                            setGlobalVariableValue({
                              key: 'USER_DETAILS',
                              value: loginResponse?.user,
                            });
                            if (
                              loginResponse?.user?._profile_of_user
                                ?.cash_min_balance
                            ) {
                              setGlobalVariableValue({
                                key: 'BALANCE_ALERT',
                                value:
                                  loginResponse?.user?._profile_of_user
                                    ?.cash_min_balance,
                              });
                            }
                            if (loginResponse?.user?.last_starting_date) {
                              setGlobalVariableValue({
                                key: 'STARTING_DATE',
                                value: loginResponse?.user?.last_starting_date,
                              });
                            } else {
                              setGlobalVariableValue({
                                key: 'STARTING_DATE',
                                value: newFormatDate(new Date(), 'get'),
                              });
                            }

                            if (loginResponse?.user?.verified) {
                              (
                                await authenticationInitiateLoginVerificationPOST.mutateAsync(
                                  { email: loginResponse?.user?.email }
                                )
                              )?.json;
                              setIsLoading(false);
                              setSendNew(true);
                              setVerifyFlow(true);
                              await waitUtil({ milliseconds: 30000 });
                              setSendNew(false);
                            } else {
                              navigation.navigate('AccountVerificationScreen', {
                                screen_from: 'login',
                                subscription_plans_id:
                                  loginResponse?.user?.subscription_plans_id,
                                expires_at:
                                  loginResponse?.user?.plan_expires_at,
                              });
                            }
                          } else {
                            setErrorMessage(
                              'Something went wrong! Please try again!'
                            );
                          }

                          setIsLoading(false);
                        } catch (err) {
                          console.error(err);
                        }
                      };
                      handler();
                    }}
                    {...GlobalStyles.ButtonStyles(theme)['Button'].props}
                    loading={isLoading}
                    style={StyleSheet.applyWidth(
                      StyleSheet.compose(
                        GlobalStyles.ButtonStyles(theme)['Button'].style,
                        {
                          backgroundColor: {
                            minWidth: Breakpoints.Laptop,
                            value: palettes.Brand['App Blue Dark'],
                          },
                          color: {
                            minWidth: Breakpoints.Laptop,
                            value: palettes.Brand.White,
                          },
                          fontFamily: {
                            minWidth: Breakpoints.Laptop,
                            value: 'OpenSans_400Regular',
                          },
                          fontSize: { minWidth: Breakpoints.Laptop, value: 18 },
                          letterSpacing: {
                            minWidth: Breakpoints.Laptop,
                            value: 1,
                          },
                          width: [
                            { minWidth: Breakpoints.Laptop, value: '15%' },
                            { minWidth: Breakpoints.Desktop, value: '10%' },
                            { minWidth: Breakpoints.BigScreen, value: '8%' },
                          ],
                        }
                      ),
                      dimensions.width
                    )}
                    title={'Login'}
                  />
                  {/* Sign Up Link */}
                  <Link
                    accessible={true}
                    onPress={() => {
                      try {
                        navigation.navigate('SignupScreen');
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                    selectable={false}
                    {...GlobalStyles.LinkStyles(theme)['New Link'].props}
                    style={StyleSheet.applyWidth(
                      StyleSheet.compose(
                        GlobalStyles.LinkStyles(theme)['New Link'].style,
                        {
                          marginTop: {
                            minWidth: Breakpoints.Laptop,
                            value: 50,
                          },
                        }
                      ),
                      dimensions.width
                    )}
                    title={'get started'}
                  />
                </View>
              )}
            </>
            {/* Verify Button */}
            <>
              {!verifyFlow ? null : (
                <View
                  style={StyleSheet.applyWidth(
                    {
                      alignItems: 'center',
                      gap: 5,
                      marginTop: 50,
                      width: '100%',
                    },
                    dimensions.width
                  )}
                >
                  {/* Error Message */}
                  <Text
                    accessible={true}
                    selectable={false}
                    {...GlobalStyles.TextStyles(theme)['Text'].props}
                    style={StyleSheet.applyWidth(
                      StyleSheet.compose(
                        GlobalStyles.TextStyles(theme)['Text'].style,
                        {
                          color: {
                            minWidth: Breakpoints.Laptop,
                            value: palettes.Brand['App Red'],
                          },
                          fontFamily: {
                            minWidth: Breakpoints.Laptop,
                            value: 'OpenSans_500Medium',
                          },
                          fontSize: [
                            { minWidth: Breakpoints.Desktop, value: 18 },
                            { minWidth: Breakpoints.Laptop, value: 14 },
                          ],
                          textAlign: {
                            minWidth: Breakpoints.Laptop,
                            value: 'center',
                          },
                        }
                      ),
                      dimensions.width
                    )}
                  >
                    {errorMessage}
                  </Text>
                  {/* Verify */}
                  <Button
                    accessible={true}
                    iconPosition={'left'}
                    onPress={() => {
                      const handler = async () => {
                        try {
                          setErrorMessage('');
                          if (otp) {
                            setIsLoading(true);
                            const loginResponse = (
                              await authenticationVerifyOTPPOST.mutateAsync({
                                email: Constants['USER_EMAIL'],
                                otp: otp,
                              })
                            )?.json;
                            if (!loginResponse?.message) {
                              if (
                                Constants['USER_DETAILS']?.subscription_plans_id
                              ) {
                                if (
                                  checkExpires(
                                    Constants['USER_DETAILS']?.plan_expires_at
                                  )
                                ) {
                                  navigation.navigate(
                                    'PaymentPlanSelectionScreen',
                                    { screen_from: 'login' }
                                  );
                                } else {
                                  setVerifyFlow(false);
                                  setErrorMessage('');
                                  setOtp('');
                                  navigation.navigate('DashboardScreen');
                                }
                              } else {
                                navigation.navigate(
                                  'PaymentPlanSelectionScreen',
                                  { screen_from: 'login' }
                                );
                              }
                            } else {
                              setErrorMessage(loginResponse?.message);
                            }
                          } else {
                            setErrorMessage('Enter OTP code');
                          }

                          setIsLoading(false);
                        } catch (err) {
                          console.error(err);
                        }
                      };
                      handler();
                    }}
                    {...GlobalStyles.ButtonStyles(theme)['Button'].props}
                    loading={isLoading}
                    style={StyleSheet.applyWidth(
                      StyleSheet.compose(
                        GlobalStyles.ButtonStyles(theme)['Button'].style,
                        {
                          backgroundColor: {
                            minWidth: Breakpoints.Laptop,
                            value: palettes.Brand['App Blue Dark'],
                          },
                          color: {
                            minWidth: Breakpoints.Laptop,
                            value: palettes.Brand.White,
                          },
                          fontFamily: {
                            minWidth: Breakpoints.Laptop,
                            value: 'OpenSans_400Regular',
                          },
                          fontSize: { minWidth: Breakpoints.Laptop, value: 18 },
                          letterSpacing: {
                            minWidth: Breakpoints.Laptop,
                            value: 1,
                          },
                          width: [
                            { minWidth: Breakpoints.Laptop, value: '15%' },
                            { minWidth: Breakpoints.Desktop, value: '10%' },
                            { minWidth: Breakpoints.BigScreen, value: '8%' },
                          ],
                        }
                      ),
                      dimensions.width
                    )}
                    title={'Verify'}
                  />
                  <View
                    style={StyleSheet.applyWidth(
                      { alignItems: 'center', paddingTop: 20, width: '100%' },
                      dimensions.width
                    )}
                  >
                    {/* Send New */}
                    <Button
                      accessible={true}
                      iconPosition={'left'}
                      onPress={() => {
                        const handler = async () => {
                          try {
                            setOtp('');
                            setSendNew(true);
                            (
                              await authenticationInitiateLoginVerificationPOST.mutateAsync(
                                { email: Constants['USER_EMAIL'] }
                              )
                            )?.json;
                            await waitUtil({ milliseconds: 30000 });
                            setSendNew(false);
                          } catch (err) {
                            console.error(err);
                          }
                        };
                        handler();
                      }}
                      {...GlobalStyles.ButtonStyles(theme)['Mobile Button']
                        .props}
                      disabled={sendNew}
                      style={StyleSheet.applyWidth(
                        StyleSheet.compose(
                          GlobalStyles.ButtonStyles(theme)['Mobile Button']
                            .style,
                          {
                            backgroundColor: [
                              {
                                minWidth: Breakpoints.Mobile,
                                value: 'rgba(0, 0, 0, 0)',
                              },
                              {
                                minWidth: Breakpoints.Laptop,
                                value: 'rgba(0, 0, 0, 0)',
                              },
                            ],
                            borderColor: [
                              {
                                minWidth: Breakpoints.Mobile,
                                value: palettes.Brand['App Blue Dark'],
                              },
                              {
                                minWidth: Breakpoints.Mobile,
                                value: sendNew
                                  ? palettes.Gray[300]
                                  : palettes.Brand['App Blue Dark'],
                              },
                            ],
                            borderWidth: 2,
                            color: [
                              {
                                minWidth: Breakpoints.Mobile,
                                value: palettes.Brand['App Blue Dark'],
                              },
                              { minWidth: Breakpoints.Laptop, value: null },
                              {
                                minWidth: Breakpoints.Mobile,
                                value: sendNew
                                  ? palettes.Gray[300]
                                  : palettes.Brand['App Blue Dark'],
                              },
                              {
                                minWidth: Breakpoints.Laptop,
                                value: sendNew
                                  ? palettes.Gray[300]
                                  : palettes.Brand['App Blue Dark'],
                              },
                            ],
                            fontFamily: {
                              minWidth: Breakpoints.Laptop,
                              value: 'OpenSans_600SemiBold',
                            },
                            letterSpacing: {
                              minWidth: Breakpoints.Laptop,
                              value: null,
                            },
                            width: [
                              { minWidth: Breakpoints.BigScreen, value: '8%' },
                              { minWidth: Breakpoints.Tablet, value: '25%' },
                              { minWidth: Breakpoints.Mobile, value: '40%' },
                            ],
                          }
                        ),
                        dimensions.width
                      )}
                      title={'Send New'}
                    />
                    <>
                      {!sendNew ? null : (
                        <Text
                          accessible={true}
                          selectable={false}
                          {...GlobalStyles.TextStyles(theme)['Text'].props}
                          style={StyleSheet.applyWidth(
                            StyleSheet.compose(
                              GlobalStyles.TextStyles(theme)['Text'].style,
                              {
                                color: palettes.Brand.White,
                                fontFamily: 'OpenSans_400Regular_Italic',
                              }
                            ),
                            dimensions.width
                          )}
                        >
                          {'Please wait 30 sec send new code'}
                        </Text>
                      )}
                    </>
                  </View>
                  <Link
                    accessible={true}
                    onPress={() => {
                      try {
                        setVerifyFlow(false);
                        setOtp('');
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                    selectable={false}
                    {...GlobalStyles.LinkStyles(theme)['Link'].props}
                    style={StyleSheet.applyWidth(
                      StyleSheet.compose(
                        GlobalStyles.LinkStyles(theme)['Link'].style,
                        {
                          color: palettes.Brand['App Green'],
                          fontFamily: [
                            {
                              minWidth: Breakpoints.Mobile,
                              value: 'OpenSans_600SemiBold',
                            },
                            {
                              minWidth: Breakpoints.Laptop,
                              value: 'Inter_600SemiBold',
                            },
                          ],
                          fontSize: { minWidth: Breakpoints.Laptop, value: 18 },
                          marginTop: 40,
                          textTransform: 'uppercase',
                        }
                      ),
                      dimensions.width
                    )}
                    title={'BACK TO LOGIN'}
                  />
                </View>
              )}
            </>
            {/* Site Info */}
            <View
              style={StyleSheet.applyWidth(
                {
                  height: { minWidth: Breakpoints.Laptop, value: '100%' },
                  left: [
                    { minWidth: Breakpoints.Desktop, value: 0 },
                    { minWidth: Breakpoints.Laptop, value: 0 },
                  ],
                  overflow: { minWidth: Breakpoints.Laptop, value: 'hidden' },
                  position: [
                    { minWidth: Breakpoints.Desktop, value: 'absolute' },
                    { minWidth: Breakpoints.Laptop, value: 'absolute' },
                  ],
                  width: { minWidth: Breakpoints.Laptop, value: '35%' },
                },
                dimensions.width
              )}
            >
              {/* Text Group */}
              <View
                style={StyleSheet.applyWidth(
                  {
                    gap: [
                      { minWidth: Breakpoints.Laptop, value: 24 },
                      { minWidth: Breakpoints.Desktop, value: 24 },
                    ],
                    paddingLeft: {
                      minWidth: Breakpoints.Desktop,
                      value: '10%',
                    },
                  },
                  dimensions.width
                )}
              >
                <Utils.CustomCodeErrorBoundary>
                  <Confetti.Confetti />
                </Utils.CustomCodeErrorBoundary>
              </View>
            </View>
          </SimpleStyleScrollView>
        )}
      </>
      <>
        {!!(dimensions.width >= Breakpoints.Laptop) ? null : (
          <SimpleStyleKeyboardAwareScrollView
            enableOnAndroid={false}
            enableResetScrollToCoords={false}
            showsVerticalScrollIndicator={true}
            viewIsInsideTabBar={false}
            enableAutomaticScroll={true}
            keyboardShouldPersistTaps={'always'}
            style={StyleSheet.applyWidth(
              {
                alignItems: [
                  { minWidth: Breakpoints.Tablet, value: 'center' },
                  { minWidth: Breakpoints.Mobile, value: 'center' },
                ],
                flex: 1,
                padding: 24,
              },
              dimensions.width
            )}
          >
            {/* Logo */}
            <View
              style={StyleSheet.applyWidth(
                {
                  alignItems: 'center',
                  height: '33%',
                  justifyContent: 'center',
                },
                dimensions.width
              )}
            >
              {/* Logo Image */}
              <Image
                {...GlobalStyles.ImageStyles(theme)['Image'].props}
                resizeMode={'contain'}
                source={imageSource(Images['logocropped'])}
                style={StyleSheet.applyWidth(
                  StyleSheet.compose(
                    GlobalStyles.ImageStyles(theme)['Image'].style,
                    {
                      height: [
                        { minWidth: Breakpoints.BigScreen, value: 250 },
                        { minWidth: Breakpoints.Mobile, value: 200 },
                      ],
                      width: [
                        { minWidth: Breakpoints.BigScreen, value: 250 },
                        { minWidth: Breakpoints.Mobile, value: 200 },
                      ],
                    }
                  ),
                  dimensions.width
                )}
              />
            </View>
            {/* Inputs */}
            <>
              {verifyFlow ? null : (
                <View
                  style={StyleSheet.applyWidth(
                    {
                      gap: [
                        { minWidth: Breakpoints.Laptop, value: 50 },
                        { minWidth: Breakpoints.Mobile, value: 50 },
                      ],
                      paddingTop: '15%',
                      width: [
                        { minWidth: Breakpoints.Laptop, value: '25%' },
                        { minWidth: Breakpoints.BigScreen, value: '15%' },
                        { minWidth: Breakpoints.Desktop, value: '17%' },
                        { minWidth: Breakpoints.Tablet, value: '30%' },
                      ],
                    },
                    dimensions.width
                  )}
                >
                  {/* Email Input */}
                  <TextInput
                    autoCapitalize={'none'}
                    autoCorrect={true}
                    changeTextDelay={500}
                    onChangeText={newEmailInputValue => {
                      const textInputValue = newEmailInputValue;
                      try {
                        setEmail(newEmailInputValue);
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                    webShowOutline={true}
                    {...GlobalStyles.TextInputStyles(theme)[
                      'Mobile Login Input'
                    ].props}
                    autoComplete={'email'}
                    keyboardType={'email-address'}
                    placeholder={'Enter email'}
                    style={StyleSheet.applyWidth(
                      GlobalStyles.TextInputStyles(theme)['Mobile Login Input']
                        .style,
                      dimensions.width
                    )}
                    value={email}
                  />
                  <View>
                    {/* Password Input */}
                    <TextInput
                      autoCapitalize={'none'}
                      autoCorrect={true}
                      changeTextDelay={500}
                      onChangeText={newPasswordInputValue => {
                        try {
                          setPassword(newPasswordInputValue);
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                      webShowOutline={true}
                      {...GlobalStyles.TextInputStyles(theme)['Mobile Password']
                        .props}
                      autoComplete={'current-password'}
                      keyboardType={'visible-password'}
                      placeholder={'Enter password'}
                      secureTextEntry={!visiblePassword}
                      style={StyleSheet.applyWidth(
                        StyleSheet.compose(
                          GlobalStyles.TextInputStyles(theme)['Mobile Password']
                            .style,
                          { paddingBottom: 12, paddingTop: 12 }
                        ),
                        dimensions.width
                      )}
                      value={password}
                    />
                    <IconButton
                      onPress={() => {
                        try {
                          setVisiblePassword(!visiblePassword);
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                      color={StyleSheet.getWidthValue(
                        [
                          {
                            minWidth: Breakpoints.Laptop,
                            value: palettes.Brand.Black,
                          },
                          {
                            minWidth: Breakpoints.Mobile,
                            value: palettes.Brand.Black,
                          },
                        ],
                        dimensions.width
                      )}
                      icon={
                        visiblePassword
                          ? 'FontAwesome/eye'
                          : 'FontAwesome/eye-slash'
                      }
                      size={StyleSheet.getWidthValue(
                        [
                          { minWidth: Breakpoints.Laptop, value: 24 },
                          { minWidth: Breakpoints.Mobile, value: 24 },
                        ],
                        dimensions.width
                      )}
                      style={StyleSheet.applyWidth(
                        {
                          position: [
                            { minWidth: Breakpoints.Laptop, value: 'absolute' },
                            { minWidth: Breakpoints.Mobile, value: 'absolute' },
                          ],
                          right: [
                            { minWidth: Breakpoints.Laptop, value: 8 },
                            { minWidth: Breakpoints.Mobile, value: 8 },
                          ],
                          top: [
                            { minWidth: Breakpoints.Mobile, value: 8 },
                            { minWidth: Breakpoints.Laptop, value: 6 },
                          ],
                        },
                        dimensions.width
                      )}
                    />
                  </View>
                  {/* Forget Password Link */}
                  <Link
                    accessible={true}
                    onPress={() => {
                      try {
                        navigation.navigate('ForgotPasswordScreen');
                        setEmail('');
                        setPassword('');
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                    selectable={false}
                    {...GlobalStyles.LinkStyles(theme)['Link'].props}
                    style={StyleSheet.applyWidth(
                      StyleSheet.compose(
                        GlobalStyles.LinkStyles(theme)['Link'].style,
                        {
                          alignSelf: 'center',
                          color: [
                            {
                              minWidth: Breakpoints.Laptop,
                              value: palettes.Brand['App Green'],
                            },
                            {
                              minWidth: Breakpoints.Mobile,
                              value: palettes.Brand['App Green'],
                            },
                          ],
                          fontFamily: [
                            {
                              minWidth: Breakpoints.Laptop,
                              value: 'OpenSans_500Medium',
                            },
                            {
                              minWidth: Breakpoints.Mobile,
                              value: 'OpenSans_500Medium',
                            },
                          ],
                          fontSize: [
                            { minWidth: Breakpoints.Desktop, value: 16 },
                            { minWidth: Breakpoints.Laptop, value: 16 },
                            { minWidth: Breakpoints.Mobile, value: 16 },
                          ],
                          textAlign: [
                            { minWidth: Breakpoints.Laptop, value: 'center' },
                            { minWidth: Breakpoints.Mobile, value: 'center' },
                          ],
                          textTransform: {
                            minWidth: Breakpoints.Laptop,
                            value: 'lowercase',
                          },
                        }
                      ),
                      dimensions.width
                    )}
                    title={'forgot password'}
                  />
                </View>
              )}
            </>
            {/* Verify View */}
            <>
              {!verifyFlow ? null : (
                <View
                  style={StyleSheet.applyWidth(
                    { alignItems: 'center', gap: 50, width: '100%' },
                    dimensions.width
                  )}
                >
                  <Text
                    accessible={true}
                    selectable={false}
                    {...GlobalStyles.TextStyles(theme)['Text'].props}
                    style={StyleSheet.applyWidth(
                      StyleSheet.compose(
                        GlobalStyles.TextStyles(theme)['Text'].style,
                        {
                          color: palettes.Brand.White,
                          fontFamily: 'OpenSans_500Medium',
                          textAlign: 'center',
                        }
                      ),
                      dimensions.width
                    )}
                  >
                    {
                      'For your security, please enter the code sent to your email. It is valid for 10 minutes.'
                    }
                  </Text>
                  <NumberInput
                    changeTextDelay={500}
                    onChangeText={newNumberInputValue => {
                      try {
                        setOtp(newNumberInputValue);
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                    webShowOutline={true}
                    {...GlobalStyles.NumberInputStyles(theme)['Number Input']
                      .props}
                    placeholder={'Enter OTP '}
                    style={StyleSheet.applyWidth(
                      StyleSheet.compose(
                        GlobalStyles.NumberInputStyles(theme)['Number Input']
                          .style,
                        {
                          backgroundColor: palettes.Brand.White,
                          color: palettes.Brand.Black,
                          fontFamily: 'OpenSans_500Medium',
                          fontSize: 16,
                          paddingBottom: 12,
                          paddingTop: 12,
                          width: '60%',
                        }
                      ),
                      dimensions.width
                    )}
                    value={otp}
                  />
                </View>
              )}
            </>
            {/* Buttons */}
            <View
              style={StyleSheet.applyWidth(
                {
                  alignItems: [
                    { minWidth: Breakpoints.Laptop, value: 'center' },
                    { minWidth: Breakpoints.Mobile, value: 'center' },
                  ],
                  flex: 1,
                  gap: [
                    { minWidth: Breakpoints.Laptop, value: 50 },
                    { minWidth: Breakpoints.Mobile, value: 20 },
                  ],
                  justifyContent: 'flex-end',
                  paddingBottom: 20,
                  width: [
                    { minWidth: Breakpoints.Laptop, value: '100%' },
                    { minWidth: Breakpoints.Mobile, value: '100%' },
                  ],
                },
                dimensions.width
              )}
            >
              {/* Error Message */}
              <Text
                accessible={true}
                selectable={false}
                {...GlobalStyles.TextStyles(theme)['Text'].props}
                style={StyleSheet.applyWidth(
                  StyleSheet.compose(
                    GlobalStyles.TextStyles(theme)['Text'].style,
                    {
                      color: [
                        {
                          minWidth: Breakpoints.Laptop,
                          value: palettes.Brand['App Red'],
                        },
                        {
                          minWidth: Breakpoints.Mobile,
                          value: palettes.Brand['App Red'],
                        },
                      ],
                      fontFamily: [
                        {
                          minWidth: Breakpoints.Laptop,
                          value: 'OpenSans_400Regular',
                        },
                        {
                          minWidth: Breakpoints.Mobile,
                          value: 'OpenSans_600SemiBold',
                        },
                      ],
                      fontSize: [
                        { minWidth: Breakpoints.Desktop, value: 18 },
                        { minWidth: Breakpoints.Laptop, value: 14 },
                      ],
                      textAlign: [
                        { minWidth: Breakpoints.Laptop, value: 'center' },
                        { minWidth: Breakpoints.Mobile, value: 'center' },
                      ],
                    }
                  ),
                  dimensions.width
                )}
              >
                {errorMessage}
              </Text>
              {/* Login */}
              <>
                {verifyFlow ? null : (
                  <Button
                    accessible={true}
                    iconPosition={'left'}
                    onPress={() => {
                      const handler = async () => {
                        try {
                          if (inputValid()) {
                            return;
                          }
                          setErrorMessage('');
                          resetGlobals(Variables, setGlobalVariableValue);
                          setIsLoading(true);
                          const loginResponse = (
                            await authenticationLoginPOST.mutateAsync({
                              email: email,
                              password: password,
                            })
                          )?.json;
                          if (loginResponse?.authToken) {
                            setGlobalVariableValue({
                              key: 'AUTH_TOKEN',
                              value: 'Bearer ' + loginResponse?.authToken,
                            });
                            setGlobalVariableValue({
                              key: 'USER_EMAIL',
                              value: email,
                            });
                            setGlobalVariableValue({
                              key: 'USER_DETAILS',
                              value: (() => {
                                const e = loginResponse?.user;
                                console.log(e);
                                return e;
                              })(),
                            });
                            if (loginResponse?.user?.last_starting_date) {
                              setGlobalVariableValue({
                                key: 'STARTING_DATE',
                                value: loginResponse?.user?.last_starting_date,
                              });
                            }
                            if (
                              loginResponse?.user?._profile_of_user
                                ?.cash_min_balance
                            ) {
                              setGlobalVariableValue({
                                key: 'BALANCE_ALERT',
                                value:
                                  loginResponse?.user?._profile_of_user
                                    ?.cash_min_balance,
                              });
                            }
                            setErrorMessage('');
                            if (
                              loginResponse?.user?.subscription_plans_id > 1
                            ) {
                              if (
                                checkExpires(
                                  loginResponse?.user?.plan_expires_at
                                )
                              ) {
                                setErrorMessage(
                                  'Your subscription has expired'
                                );
                              } else {
                                (
                                  await authenticationInitiateLoginVerificationPOST.mutateAsync(
                                    { email: loginResponse?.user?.email }
                                  )
                                )?.json;
                                setVerifyFlow(true);
                                setPassword('');
                              }
                            } else {
                              setErrorMessage(
                                'Active subscription required to log in'
                              );
                            }
                          } else {
                            setErrorMessage(loginResponse?.message);
                          }

                          setIsLoading(false);
                        } catch (err) {
                          console.error(err);
                        }
                      };
                      handler();
                    }}
                    {...GlobalStyles.ButtonStyles(theme)['Mobile Button'].props}
                    loading={isLoading}
                    style={StyleSheet.applyWidth(
                      StyleSheet.compose(
                        GlobalStyles.ButtonStyles(theme)['Mobile Button'].style,
                        {
                          width: [
                            { minWidth: Breakpoints.BigScreen, value: '8%' },
                            { minWidth: Breakpoints.Tablet, value: '25%' },
                          ],
                        }
                      ),
                      dimensions.width
                    )}
                    title={'Login'}
                  />
                )}
              </>
              {/* Verify Button */}
              <>
                {!verifyFlow ? null : (
                  <View
                    style={StyleSheet.applyWidth(
                      {
                        alignItems: 'center',
                        gap: 5,
                        marginTop: 50,
                        width: '100%',
                      },
                      dimensions.width
                    )}
                  >
                    {/* Verify */}
                    <Button
                      accessible={true}
                      iconPosition={'left'}
                      onPress={() => {
                        const handler = async () => {
                          try {
                            setErrorMessage('');
                            if (otp) {
                              setIsLoading(true);
                              const otpResponse = (
                                await authenticationVerifyOTPPOST.mutateAsync({
                                  email: Constants['USER_EMAIL'],
                                  otp: otp,
                                })
                              )?.json;
                              if (!otpResponse?.message) {
                                setOtp('');
                                setVerifyFlow(false);
                                navigation.navigate('DashboardScreen');
                              } else {
                                setErrorMessage(otpResponse?.message);
                              }
                            } else {
                              setErrorMessage('Enter OTP code');
                            }

                            setIsLoading(false);
                          } catch (err) {
                            console.error(err);
                          }
                        };
                        handler();
                      }}
                      {...GlobalStyles.ButtonStyles(theme)['Mobile Button']
                        .props}
                      loading={isLoading}
                      style={StyleSheet.applyWidth(
                        StyleSheet.compose(
                          GlobalStyles.ButtonStyles(theme)['Mobile Button']
                            .style,
                          {
                            width: [
                              { minWidth: Breakpoints.BigScreen, value: '8%' },
                              { minWidth: Breakpoints.Tablet, value: '25%' },
                              { minWidth: Breakpoints.Mobile, value: '40%' },
                            ],
                          }
                        ),
                        dimensions.width
                      )}
                      title={'Verify'}
                    />
                    <View
                      style={StyleSheet.applyWidth(
                        { alignItems: 'center', paddingTop: 20, width: '100%' },
                        dimensions.width
                      )}
                    >
                      {/* Send New */}
                      <Button
                        accessible={true}
                        iconPosition={'left'}
                        onPress={() => {
                          const handler = async () => {
                            try {
                              setOtp('');
                              setSendNew(true);
                              (
                                await authenticationInitiateLoginVerificationPOST.mutateAsync(
                                  { email: Constants['USER_EMAIL'] }
                                )
                              )?.json;
                              await waitUtil({ milliseconds: 30000 });
                              setSendNew(false);
                            } catch (err) {
                              console.error(err);
                            }
                          };
                          handler();
                        }}
                        {...GlobalStyles.ButtonStyles(theme)['Mobile Button']
                          .props}
                        disabled={sendNew}
                        style={StyleSheet.applyWidth(
                          StyleSheet.compose(
                            GlobalStyles.ButtonStyles(theme)['Mobile Button']
                              .style,
                            {
                              backgroundColor: 'rgba(0, 0, 0, 0)',
                              borderColor: [
                                {
                                  minWidth: Breakpoints.Mobile,
                                  value: palettes.Brand['App Blue Dark'],
                                },
                                {
                                  minWidth: Breakpoints.Mobile,
                                  value: sendNew
                                    ? palettes.Gray[300]
                                    : palettes.Brand['App Blue Dark'],
                                },
                              ],
                              borderWidth: 2,
                              color: [
                                {
                                  minWidth: Breakpoints.Mobile,
                                  value: palettes.Brand['App Blue Dark'],
                                },
                                {
                                  minWidth: Breakpoints.Mobile,
                                  value: sendNew
                                    ? palettes.Gray[300]
                                    : palettes.Brand['App Blue Dark'],
                                },
                              ],
                              width: [
                                {
                                  minWidth: Breakpoints.BigScreen,
                                  value: '8%',
                                },
                                { minWidth: Breakpoints.Tablet, value: '25%' },
                                { minWidth: Breakpoints.Mobile, value: '40%' },
                              ],
                            }
                          ),
                          dimensions.width
                        )}
                        title={'Send New'}
                      />
                      <>
                        {!sendNew ? null : (
                          <Text
                            accessible={true}
                            selectable={false}
                            {...GlobalStyles.TextStyles(theme)['Text'].props}
                            style={StyleSheet.applyWidth(
                              StyleSheet.compose(
                                GlobalStyles.TextStyles(theme)['Text'].style,
                                {
                                  color: palettes.Brand.White,
                                  fontFamily: 'OpenSans_400Regular_Italic',
                                }
                              ),
                              dimensions.width
                            )}
                          >
                            {'Please wait 30 sec send new code'}
                          </Text>
                        )}
                      </>
                    </View>
                    <Link
                      accessible={true}
                      onPress={() => {
                        try {
                          setVerifyFlow(false);
                          setOtp('');
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                      selectable={false}
                      {...GlobalStyles.LinkStyles(theme)['Link'].props}
                      style={StyleSheet.applyWidth(
                        StyleSheet.compose(
                          GlobalStyles.LinkStyles(theme)['Link'].style,
                          {
                            color: palettes.Brand['App Green'],
                            fontFamily: 'OpenSans_600SemiBold',
                            marginTop: 40,
                            textTransform: 'uppercase',
                          }
                        ),
                        dimensions.width
                      )}
                      title={'BACK TO LOGIN'}
                    />
                  </View>
                )}
              </>
            </View>
          </SimpleStyleKeyboardAwareScrollView>
        )}
      </>
    </ScreenContainer>
  );
};

export default withTheme(LoginScreen);
